import { computed, reactive, watch } from 'vue'
import api from '@/api'
import searchQueryState from './SearchQuery'

const searchResultState = reactive({
  loading: false,
  allItemsLoaded: false,
  totalCount: 0,
  hasResults: false,
  items: [],
  termsFacet: [],
  searchResults: {},
  trackId: '',
  skipInitialFetch: false
})

export default {
  // Seed the search result state with data rendered server-side so the first
  // render reuses it instead of re-fetching (avoids a spinner flash). The skip
  // flag is reset after the synchronous mount tick so later queries fetch normally.
  initialize(initialResult) {
    if (!initialResult || !Array.isArray(initialResult.items)) {
      return
    }

    const { searchQuery } = searchQueryState.searchQueryUse()
    const skip = searchQuery.skip || 0
    const take = searchQuery.take || 8
    const totalCount = initialResult.totalCount || 0

    searchResultState.items = initialResult.items
    searchResultState.totalCount = totalCount
    searchResultState.termsFacet = initialResult.termsFacet || []
    searchResultState.trackId = initialResult.trackId || ''
    searchResultState.hasResults = totalCount > 0
    searchResultState.allItemsLoaded = skip + take >= totalCount
    searchResultState.loading = false
    searchResultState.skipInitialFetch = true

    Promise.resolve().then(() => {
      searchResultState.skipInitialFetch = false
    })
  },
  useSearchResult() {
    const { searchQuery, incrementPage } = searchQueryState.searchQueryUse()
    const state = searchResultState
    const { take } = searchQuery

    async function fetchData() {
      const { skip } = searchQuery
      state.items = []
      state.totalCount = ''
      state.allItemsLoaded = false
      state.hasResults = false
      const { searchType } = searchQuery
      const response = await api.getSearchItems(searchQuery)
      state.searchResults = response.data.searchResults
      const activeSearchResult = state.searchResults
        .find((x) => x.type === searchType)
      state.totalCount = activeSearchResult.totalCount
      state.items = activeSearchResult.items
      state.termsFacet = activeSearchResult.termsFacet
      state.trackId = activeSearchResult.trackId
      state.hasResults = state.searchResults.some(({ totalCount }) => totalCount > 0)

      if (skip + take >= state.totalCount) {
        state.allItemsLoaded = true
      }
    }

    async function fetchExtraItems() {
      const { skip } = searchQuery
      const searchRequest = {
        language: searchQuery.language,
        skip,
        take,
        activeCategories: searchQuery.activeCategories,
        terms: searchQuery.terms,
        sorting: searchQuery.sorting
      }

      if (searchQuery.searchType === 'recipe') {
        const response = await api.getRecipes(searchRequest)
        state.items.push(...response.data.items)
      }

      if (searchQuery.searchType === 'product') {
        const response = await api.getProducts(searchRequest)
        state.items.push(...response.data.items)
      }

      if (skip + take >= state.totalCount) {
        state.allItemsLoaded = true
      }
    }

    async function search() {
      if (state.skipInitialFetch) {
        return
      }

      if (!state.loading) {
        state.loading = true
        fetchData().then(() => {
          state.loading = false
        })
      }
    }

    async function searchMore() {
      if (state.totalCount === state.items.length || state.allItemsLoaded) {
        return
      }

      if (!state.loading) {
        incrementPage()
        state.loading = true
        fetchExtraItems().then(() => {
          state.loading = false
        })
      }
    }
    watch(
      () => [searchQuery.updated],
      () => {
        if (!state.loading && searchQuery.searchType) {
          search()
        }
      }, {
        immediate: true
      }
    )

    return {
      allItemsLoaded: computed(() => state.allItemsLoaded),
      loading: computed(() => state.loading),
      searchResults: computed(() => state.searchResults),
      totalCount: computed(() => state.totalCount),
      items: computed(() => state.items),
      termsFacet: computed(() => state.termsFacet),
      trackId: computed(() => state.trackId),
      hasResults: computed(() => state.hasResults),
      search,
      searchMore
    }
  }
}
